import api from 'api';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import BaseInput from 'components/common/BaseInput';
import SelectDocumentTagDropdown from './SelectDocumentTagDropdown';
import { Label } from '../../../../../../../BACKOFFICEFLOW/CLIENTVIEW/ClientView.styled';
import Button from '../../../../../../../Button';
import IconBtn from 'components/common/IconBtn';
import { ReactComponent as EditIcon } from 'assets/icons/B2B/border_color.svg';
import { Ticket } from 'api/models/response/Ticket';
import { AppDocument } from 'api';
import { useWindowSize } from 'hooks/useWindowSize';
import * as S from '../../../../Dropdowns.styled';

type Props = {
    ticket: Ticket;
    fetchTickets: (appID: string) => void;
    docsList: AppDocument[];
};

const EditTicketPopover = ({ ticket, fetchTickets, docsList }: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedDocument, setSelectedDocument] = useState<string>('');
    const [ticketDetails, setTicketDetails] = useState<string>(ticket.text);
    const [document, setDocument] = useState<AppDocument>();
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
        if (docsList.length) {
            const document = docsList.find((doc) => doc.id === ticket.document_id);
            if (document) {
                setSelectedDocument(document.document_name);
                setDocument(document);
            }
        }
        //eslint-disable-next-line
    }, [docsList]);

    const handleSaveChanges = async () => {
        //if only editing the text of the ticket
        if (document?.document_name === selectedDocument) {
            try {
                const payload = {
                    id: ticket.id,
                    text: ticketDetails,
                };
                await api.tickets.editTicket(payload);
                if (activeApp) {
                    fetchTickets(activeApp.id.toString());
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            //create new ticket
            if (document) {
                const newDocument = docsList.find((doc) => doc.document_name === selectedDocument);
                if (newDocument) {
                    try {
                        const payload = {
                            document_id: newDocument.id,
                            text: ticketDetails,
                        };
                        await api.tickets.createTicket(payload);

                        //delete old ticket
                        try {
                            await api.tickets.deleteTicket(ticket.id.toString());
                            if (activeApp) {
                                fetchTickets(activeApp.id.toString());
                            }
                        } catch (e) {
                            console.error(e);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        }

        handleClose();
    };

    return (
        <>
            <S.LeftDropdown
                isActive={!!anchorEl}
                onClick={(event) => {
                    handleClick(event);
                }}
            >
                <IconBtn style={{ padding: '0px', height: '20px', width: '20px' }}>
                    <EditIcon fill="#282828" />
                </IconBtn>
            </S.LeftDropdown>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                marginThreshold={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'visible',
                            mt: '8px',
                            minWidth: '7rem',
                            padding: '0',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.09)',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <S.Container mobile={mobile}>
                    <S.Row style={{ justifyContent: 'space-between', gap: '1rem' }}>
                        <Label style={{ fontSize: '16px' }}>
                            {mobile
                                ? t('document', { lng: language })
                                : t('tagToDocument', { lng: language })}
                        </Label>

                        <SelectDocumentTagDropdown
                            mobile={mobile}
                            documents={docsList}
                            selectedDocument={selectedDocument}
                            handleSelectedDocument={setSelectedDocument}
                        />
                    </S.Row>
                    <BaseInput
                        fullWidth
                        size="medium"
                        value={ticketDetails}
                        onChange={(event) => setTicketDetails(event.target.value)}
                        label={`*${t('ticketDetails', { lng: language })}`}
                    />
                    <S.Row style={{ gap: '2rem', justifyContent: 'center' }}>
                        <Button
                            disabled={
                                selectedDocument === document?.document_name &&
                                ticket.text === ticketDetails
                            }
                            border="purple"
                            color="purple"
                            onClick={() => {
                                handleSaveChanges();
                            }}
                        >
                            {t('saveTicketAndNotify', { lng: language })}
                        </Button>
                    </S.Row>
                </S.Container>
            </Popover>
        </>
    );
};

export default EditTicketPopover;
